import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Shield, Check, Edit } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";

const formatPhone = (value: string) => {
  const numbers = (value || "").replace(/\D/g, "");
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

export default function AlunoPerfilContato() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { profile } = useProfile();

  const userEmail = user?.email ?? "—";
  const userPhone = profile?.phone || "";
  const emailVerified = !!user?.email_confirmed_at;

  const [editingField, setEditingField] = useState<'phone' | null>(null);
  const [phone, setPhone] = useState(userPhone);
  const [verifyingField, setVerifyingField] = useState<'email' | 'phone' | null>(null);
  const [code, setCode] = useState("");

  const handleSendCode = (type: 'email' | 'phone') => {
    setVerifyingField(type);
    setCode("");
    toast({
      title: "Em breve",
      description: "A verificação por código será implementada em breve.",
    });
  };

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/perfil")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground">Informações de Contato</h1>
          <p className="text-sm text-muted-foreground">Gerencie e verifique seus dados</p>
        </div>
      </motion.div>

      {/* E-mail */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-5 border border-border mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">E-mail</p>
              {emailVerified ? (
                <span className="text-xs text-success flex items-center gap-1"><Check className="w-3 h-3" /> Verificado</span>
              ) : (
                <span className="text-xs text-destructive">Não verificado</span>
              )}
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{userEmail}</p>
        <p className="text-xs text-muted-foreground mt-2">O e-mail não pode ser alterado por aqui.</p>
      </motion.div>

      {/* Telefone */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card rounded-2xl p-5 border border-border mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
              <Phone className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Telefone / WhatsApp</p>
              <span className="text-xs text-muted-foreground">Verificação em breve</span>
            </div>
          </div>
          {editingField !== 'phone' && (
            <Button variant="ghost" size="icon-sm" onClick={() => { setEditingField('phone'); setPhone(userPhone); }}>
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>

        {editingField === 'phone' ? (
          <div className="space-y-3">
            <Input value={formatPhone(phone)} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 99999-9999" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => { navigate("/aluno/perfil/editar"); }}>Salvar no perfil</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}>Cancelar</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{userPhone ? formatPhone(userPhone) : "Não informado"}</p>
        )}
      </motion.div>

      {/* Privacy Notice */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="bg-primary/5 rounded-xl p-4 border border-primary/10">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Privacidade garantida:</strong> Seus dados de contato só são compartilhados com empresas quando você aplica para uma vaga ou aceita um contato mediado pelo Corre Digital.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
